/* eslint-disable no-unsafe-optional-chaining */
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { uniqueId } from 'lodash';
import { call, getContext, put, takeLatest } from 'redux-saga/effects';
import { AdvancedSetting } from 'api/advanced-settings/settings-types';
import { ProjectId } from 'api/models';
import { Project, ProjectBaseline, ProjectPhase } from 'api/project';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { DependentEntityPayload, Task as TaskApi } from 'api/tasks';
import { GanttTask } from 'components/organisms/projects/project-plan/timeline/types';
import { sagaTaskDescriptor } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import {
  ProjectSprintDatesActionPayload,
  setGanttRefAction,
  updateGanttDatesAction,
  updateGanttSlackAction,
  updateProjectSprintDatesAction,
  updateTaskPrivacyInGanttAction,
} from 'data/projects/actions';
import { formatDate, getDueDateForGantt } from 'data/projects/gantt/dates';
import {
  GanttType,
  getGanttPhaseId,
  getGanttTaskId,
} from 'data/projects/gantt/ids';
import {
  getPhaseList,
  getTaskList,
  normaliseTasks,
} from 'data/projects/gantt/list-helpers';
import { loadProjectPlanTimelineHelpers } from 'data/projects/gantt/timeline-helpers-loader';
import {
  CreateDependencyActionPayload,
  CreateSubTaskSuccessActionPayload,
  dependencyRemovedAction,
  FetchTaskActionPayload,
  removeAsSubtaskSuccessAction,
  UpdateTaskActionPayload,
  updateTaskSagaAction,
  dependenciesRemovedAction,
  updateSprintSagaAction,
  UpdateSprintActionPayload,
  ResetEntityDatesActionPayload,
  resetEntityDatesAction,
  updateTaskRecordSagaAction,
  updateTaskRecordWithLatestDataSagaAction,
  dependenciesOperationsAction,
  dependencyCreationThroughTimeline,
  bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction,
  dependencyCreatedAction,
  deleteDependencyRelationsSagaAction,
  addTaskBelowSagaAction,
  addSubTaskSagaAction,
  removeTemporaryTaskSagaAction,
  bulkUpdateWbsSagaAction,
} from 'data/tasks/actions';
import { Task } from 'data/tasks/schema';
import { createSubTaskSuccess } from 'data/tasks/slice';
import {
  getQueryAdvancedSettingsConfigKey,
  getNewDependenciesEnabledSetting,
} from 'queries/advanced-settings/advanced-settings';
import { getProjectTasksQueryKey } from 'queries/common';
import { getProjectBaselineQueryKey } from 'queries/project-baseline-mutation';
import { logger } from 'services';
import {
  DateShiftUpdates,
  DependencyTypesEnum,
  EntityType,
  List,
} from 'utils/date-shift';

type TimelineHelpersModule = Awaited<
  ReturnType<typeof loadProjectPlanTimelineHelpers>
>;

let computeSlack: TimelineHelpersModule['computeSlack'];
let convertToGanttStoreFormat: TimelineHelpersModule['convertToGanttStoreFormat'];
let convertToGanttTask: TimelineHelpersModule['convertToGanttTask'];
let getExtremeDates: TimelineHelpersModule['getExtremeDates'];
let getGanttPhaseList: TimelineHelpersModule['getGanttPhaseList'];
let getGanttTaskListWithoutBaselines: TimelineHelpersModule['getGanttTaskListWithoutBaselines'];

interface RemoveAsSubtaskPayload extends FetchTaskActionPayload {
  phaseId?: number;
}

function setGanttRefContext({ payload }: PayloadAction<any>) {
  sagaTaskDescriptor.setContext({
    ganttRef: payload,
  });
}

export function* getGanttRefContext() {
  return yield getContext('ganttRef');
}

function* resetEntityDates({
  payload,
}: PayloadAction<ResetEntityDatesActionPayload>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef || !ganttRef.instance) {
    return;
  }
  const { entityId, data, type: entityType } = payload;
  const { startDate, dueDate } = data;
  const ganttId = `${
    entityType === EntityType.Task
      ? GanttType.TaskBar
      : entityType === EntityType.Sprint
      ? GanttType.Sprint
      : GanttType.ProjectPhaseBar
  }-${entityId}`;

  if (!ganttRef || !ganttRef.instance) {
    return;
  }
  const { taskStore } = ganttRef.instance;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');
  const recordDataUpdates = {};

  if (startDate || startDate === null) {
    record.set('isCalculatedStartDate', !startDate);
    record.set('startDate', startDate);
  }
  if (dueDate || dueDate === null) {
    record.set('isCalculatedEndDate', !dueDate);
    record.set('endDate', dueDate);
  }

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });
}
function* onUpdateTask({
  payload,
}: PayloadAction<UpdateTaskActionPayload>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }
  const { taskId, data } = payload;
  const { taskName, fields, startDate, dueDate, progress, type } = data;
  const { private: isPrivate } = data;
  const ganttId = `${GanttType.TaskBar}-${taskId}`;
  const instance = ganttRef?.instance;

  if (!instance) {
    return;
  }
  const { taskStore } = instance;
  const record = taskStore.getById(ganttId);

  if (!record) {
    return;
  }

  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  if (taskName) {
    record.set({
      title: taskName,
      name: taskName,
    });
    recordDataUpdates.name = taskName;
  }
  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (startDate || startDate === null) {
    record.set('isCalculatedStartDate', !startDate);
    record.set('startDate', startDate);
  }
  if (dueDate || dueDate === null) {
    record.set('isCalculatedEndDate', !dueDate);
    record.set('endDate', getDueDateForGantt(dueDate));
  }

  if (fields) {
    const updatedFieldIds = fields.map((_field) => _field.fieldId);
    const updatedFields = fields.map((_update) => ({
      ...recordData.fields.find((field) => field.fieldId === _update.fieldId),
      ..._update,
    }));

    recordDataUpdates.fields = [
      ...recordData.fields.filter(
        (_field) => !updatedFieldIds.includes(_field.fieldId),
      ),
      ...updatedFields,
    ];
  }
  if (progress) {
    recordDataUpdates.progress = progress;
    record.set('percentDone', progress);
  }

  if (startDate) {
    recordDataUpdates.startDate = startDate;
  }

  if (dueDate) {
    recordDataUpdates.dueDate = dueDate;
  }

  if (type) {
    recordDataUpdates.type = type;
  }

  if (isPrivate) {
    recordDataUpdates.private = isPrivate;
  }

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });
}

function* onUpdateTaskPrivacy({
  payload,
}: PayloadAction<{ taskId: number; private: boolean }>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }
  const { taskId, private: isPrivate } = payload;
  const ganttId = `${GanttType.TaskBar}-${taskId}`;
  const instance = ganttRef?.instance;

  if (!instance) {
    return;
  }
  const { taskStore } = instance;
  const record = taskStore.getById(ganttId);

  if (!record) {
    return;
  }

  const recordData = record.get('data');

  record.set('data', {
    ...recordData,
    private: isPrivate,
  });

  const currentCls = record.cls.toString();

  let updatedCls = currentCls.replace(/\bgantt-private-row\b/g, '').trim();

  if (isPrivate) {
    updatedCls += ' gantt-private-row';
  }

  record.cls = updatedCls;
}

function* onUpdateSprint({
  payload,
}: PayloadAction<UpdateSprintActionPayload>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }
  const { sprintId, data } = payload;
  const { sprintName, startDate, dueDate, progress, fields } = data;
  const ganttId = `${GanttType.Sprint}-${sprintId}`;
  const { taskStore } = ganttRef.instance;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  if (sprintName) {
    record.set({
      title: sprintName,
      name: sprintName,
    });
    recordDataUpdates.name = sprintName;
  }
  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (startDate || startDate === null) {
    record.set('isCalculatedStartDate', !startDate);
    record.set('startDate', startDate);
  }
  if (dueDate || dueDate === null) {
    record.set('isCalculatedEndDate', !dueDate);
    record.set('endDate', dueDate);
  }

  if (fields) {
    const updatedFieldIds = fields.map((_field) => _field.fieldId);
    const updatedFields = fields.map((_update) => ({
      ...recordData.fields.find((field) => field.fieldId === _update.fieldId),
      ..._update,
    }));

    recordDataUpdates.fields = [
      ...recordData.fields.filter(
        (_field) => !updatedFieldIds.includes(_field.fieldId),
      ),
      ...updatedFields,
    ];
  }

  if (progress) {
    recordDataUpdates.progress = progress;
    record.set('percentDone', progress);
  }

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });
}

const updateGanttDates = (
  ganttRef,
  ganttId,
  currentEntity,
  startDate,
  dueDate,
) => {
  const {
    taskStore,
    // project
  } = ganttRef?.instance;
  const record = taskStore.getById(ganttId);

  if (!record) {
    return [];
  }

  const spanDates: Date[] = [
    new Date(startDate || currentEntity.startDate),
    new Date(dueDate || currentEntity.dueDate),
  ];

  record.setStartDate(formatDate(startDate || currentEntity.startDate));
  record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));

  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  recordDataUpdates.startDate = startDate || currentEntity.startDate;
  recordDataUpdates.dueDate = dueDate || currentEntity.dueDate;

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });

  return spanDates as [Date, Date];
};

function* onUpdateGanttDates({
  payload,
}: PayloadAction<{
  phaseUpdates: DateShiftUpdates;
  taskUpdates: DateShiftUpdates;
  phaseList: List;
  taskList: List;
  projectPhases: ProjectPhase[];
  sprintUpdates: DateShiftUpdates;
  sprintList: List;
}>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }

  let extremeProjectPhaseDates: Date[] = [];
  const {
    phaseUpdates,
    taskUpdates,
    phaseList,
    taskList,
    projectPhases,
    sprintUpdates,
    sprintList,
  } = payload;

  if (projectPhases && projectPhases.length > 0) {
    projectPhases.forEach((phase) => {
      extremeProjectPhaseDates = getExtremeDates(
        extremeProjectPhaseDates as [Date, Date],
        [new Date(phase.startDate), new Date(phase.dueDate)],
      );
    });
  }

  Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
    const ganttId = getGanttPhaseId(id);

    const spanDates = updateGanttDates(
      ganttRef,
      ganttId,
      phaseList[id],
      startDate,
      dueDate,
    );

    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      spanDates as [Date, Date],
    );
  });
  Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
    const ganttId = getGanttTaskId(id);

    const spanDates = updateGanttDates(
      ganttRef,
      ganttId,
      taskList[id],
      startDate,
      dueDate,
    );

    extremeProjectPhaseDates = getExtremeDates(
      extremeProjectPhaseDates as [Date, Date],
      spanDates as [Date, Date],
    );
  });
  if (sprintUpdates && sprintList) {
    Object.values(sprintUpdates)?.forEach(({ startDate, dueDate, id }) => {
      const ganttId = `${GanttType.Sprint}-${id}`;

      const spanDates = updateGanttDates(
        ganttRef,
        ganttId,
        sprintList[id],
        startDate,
        dueDate,
      );

      extremeProjectPhaseDates = getExtremeDates(
        extremeProjectPhaseDates as [Date, Date],
        spanDates as [Date, Date],
      );
    });
  }

  ganttRef?.instance.setTimeSpan(
    extremeProjectPhaseDates[0],
    extremeProjectPhaseDates[1],
  );
}

function* onDependencyCreated({
  payload,
}: PayloadAction<Omit<CreateDependencyActionPayload, 'projectId'>>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId, dependentTaskId, dependencyType, lag } = payload;

  const { dependencyStore, taskStore } = ganttRef?.instance;
  const ganttId = `${GanttType.TaskBar}-${taskId}`;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  recordData.dependencies = [
    ...recordData.dependencies,
    {
      taskId: dependentTaskId,
      dependencyType,
      lag,
    },
  ];

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });

  dependencyStore.add({
    id: `${taskId}-${dependentTaskId}`,
    fromEvent: `${GanttType.TaskBar}-${dependentTaskId}`,
    toEvent: `${GanttType.TaskBar}-${taskId}`,
    type: dependencyType,
    lag,
  });
}

function* onDependencyRemoved({
  payload,
}: PayloadAction<Omit<CreateDependencyActionPayload, 'projectId'>>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId, dependentTaskId } = payload;

  const { dependencyStore } = ganttRef?.instance;

  dependencyStore.remove(`${taskId}-${dependentTaskId}`);
}

function* onDependeciesRemoved({
  payload,
}: PayloadAction<
  Omit<
    {
      taskId: number;
      dependentTaskIds: number[];
    },
    'projectId'
  >
>): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskId, dependentTaskIds } = payload;

  const { dependencyStore } = ganttRef?.instance;

  dependentTaskIds.forEach((dependentTaskId) => {
    dependencyStore.remove(`${taskId}-${dependentTaskId}`);
  });
}

function* bulkDependencyDeletionForPhasesAndTasksFromTimeline({
  payload,
}: PayloadAction<{
  dependencyRelationsToDelete: string[];
}>): Generator {
  const { dependencyRelationsToDelete } = payload;

  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { dependencyStore, project, taskStore } = ganttRef?.instance;

  dependencyRelationsToDelete.forEach((relation) => {
    const dependencyData = dependencyStore.getById(relation);
    const [id, dependentId] = relation.split('-');

    const record =
      taskStore.getById(`${GanttType.ProjectPhaseBar}-${id}`) ??
      taskStore.getById(`${GanttType.TaskBar}-${id}`);
    const recordData = record.get('data');

    recordData.dependencies = recordData.dependencies.filter(
      (dep) => dep.taskId !== Number(dependentId),
    );

    recordData.predecessorDependencies =
      recordData.predecessorDependencies?.filter(
        (dep) => dep.id !== Number(dependentId),
      );

    if (dependencyData) {
      dependencyData.remove();
    }
  });

  // Commit changes to ensure the UI reflects the changes
  project.commitAsync();
}

function* onDependencyOperations({
  payload,
}: PayloadAction<{
  id: number;
  payload: DependentEntityPayload[];
  type: EntityType;
  fromTemplate?: boolean;
}>): Generator {
  const { id, payload: dependentTasksPayload, type, fromTemplate } = payload;

  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { dependencyStore, taskStore } = ganttRef?.instance;
  const ganttId =
    type === EntityType.Phase
      ? `${GanttType.ProjectPhaseBar}-${id}`
      : `${GanttType.TaskBar}-${id}`;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');

  const ganttType =
    type === EntityType.Phase ? GanttType.ProjectPhaseBar : GanttType.TaskBar;

  dependentTasksPayload.forEach((dependency) => {
    if (dependency.operation === 'ADD') {
      const dependencyData: {
        id: string;
        fromEvent: string;
        toEvent: string;
        type: number;
        lag?: number;
      } = {
        id: `${id}-${dependency.dependentEntityId}`,
        fromEvent: `${ganttType}-${dependency.dependentEntityId}`,
        toEvent: `${ganttType}-${id}`,
        type: DependencyTypesEnum[dependency.dependencyType],
      };

      const predecessorRecord = taskStore.getById(
        `${ganttType}-${dependency.dependentEntityId}`,
      );
      const predecessorRecordData = predecessorRecord.get('data');

      if (dependency.lag) {
        dependencyData.lag = dependency.lag;
      }

      dependencyStore.add(dependencyData);

      if (type === EntityType.Phase) {
        if (fromTemplate) {
          recordData.dependentProjectPhases = [
            ...(recordData.dependentProjectPhases || []),
            {
              phaseId: dependency.dependentEntityId,
              dependencyType: DependencyTypesEnum[dependency.dependencyType],
              lag: dependency.lag ? dependency.lag : null,
              taskName:
                predecessorRecordData.taskName ??
                predecessorRecordData.projectPhaseName,
            },
          ];
        } else {
          recordData.dependencies = [
            ...(recordData.dependencies || []),
            {
              phaseId: dependency.dependentEntityId,
              dependencyType: DependencyTypesEnum[dependency.dependencyType],
              lag: dependency.lag ? dependency.lag : null,
              taskName: predecessorRecordData.name,
            },
          ];
        }
      } else if (fromTemplate) {
        recordData.dependentTasks = [
          ...(recordData.dependentTasks || []),
          {
            taskId: dependency.dependentEntityId,
            dependencyType: DependencyTypesEnum[dependency.dependencyType],
            lag: dependency.lag ? dependency.lag : null,
            taskName:
              predecessorRecordData.taskName ??
              predecessorRecordData.projectPhaseName,
          },
        ];
      } else {
        recordData.dependencies = [
          ...(recordData.dependencies || []),
          {
            taskId: dependency.dependentEntityId,
            dependencyType: DependencyTypesEnum[dependency.dependencyType],
            lag: dependency.lag ? dependency.lag : null,
            taskName: predecessorRecordData.name,
          },
        ];
      }

      if (fromTemplate) {
        recordData.predecessorDependencies = [
          ...(recordData.predecessorDependencies || []),
          {
            wbs: predecessorRecordData.wbs,
            lag: dependency.lag ? dependency.lag : null,
            dependencyType: DependencyTypesEnum[dependency.dependencyType],
            id: dependency.dependentEntityId,
            name:
              predecessorRecordData.taskName ??
              predecessorRecordData.projectPhaseName,
          },
        ];
      } else {
        recordData.predecessorDependencies = [
          ...(recordData.predecessorDependencies || []),
          {
            wbs: predecessorRecordData.wbs,
            lag: dependency.lag ? dependency.lag : null,
            dependencyType: DependencyTypesEnum[dependency.dependencyType],
            id: dependency.dependentEntityId,
            name: predecessorRecordData.name,
          },
        ];
      }
    }
    if (dependency.operation === 'REMOVE') {
      dependencyStore.remove(`${id}-${dependency.dependentEntityId}`);

      if (type === EntityType.Phase) {
        if (fromTemplate) {
          recordData.dependentProjectPhases =
            recordData.dependentProjectPhases?.filter(
              (dep) => dep.phaseId !== dependency.dependentEntityId,
            );
        } else {
          recordData.dependencies = recordData.dependencies.filter(
            (dep) => dep.phaseId !== dependency.dependentEntityId,
          );
        }
      } else if (fromTemplate) {
        recordData.dependentTasks = recordData.dependentTasks?.filter(
          (dep) => dep.taskId !== dependency.dependentEntityId,
        );
      } else {
        recordData.dependencies = recordData.dependencies.filter(
          (dep) => dep.taskId !== dependency.dependentEntityId,
        );
      }
      recordData.predecessorDependencies =
        recordData.predecessorDependencies?.filter(
          (dep) => dep.id !== dependency.dependentEntityId,
        );
    }
    if (dependency.operation === 'UPDATE') {
      dependencyStore.remove(`${id}-${dependency.dependentEntityId}`);
      const dependencyData: {
        id: string;
        fromEvent: string;
        toEvent: string;
        type: number;
        lag?: number;
      } = {
        id: `${id}-${dependency.dependentEntityId}`,
        fromEvent: `${ganttType}-${dependency.dependentEntityId}`,
        toEvent: `${ganttType}-${id}`,
        type: DependencyTypesEnum[dependency.dependencyType],
      };

      if (dependency.lag) {
        dependencyData.lag = dependency.lag;
      }

      dependencyStore.add(dependencyData);

      if (type === EntityType.Phase) {
        if (fromTemplate) {
          recordData.dependentProjectPhases =
            recordData.dependentProjectPhases.map((dep) =>
              dep.phaseId === dependency.dependentEntityId
                ? {
                    ...dep,
                    dependencyType:
                      DependencyTypesEnum[dependency.dependencyType],
                    lag: dependency.lag,
                  }
                : dep,
            );
        } else {
          recordData.dependencies = recordData.dependencies.map((dep) =>
            dep.phaseId === dependency.dependentEntityId
              ? {
                  ...dep,
                  dependencyType:
                    DependencyTypesEnum[dependency.dependencyType],
                  lag: dependency.lag,
                }
              : dep,
          );
        }
      } else if (fromTemplate) {
        recordData.dependentTasks = recordData.dependentTasks.map((dep) =>
          dep.taskId === dependency.dependentEntityId
            ? {
                ...dep,
                dependencyType: DependencyTypesEnum[dependency.dependencyType],
                lag: dependency.lag,
              }
            : dep,
        );
      } else {
        recordData.dependencies = recordData.dependencies.map((dep) =>
          dep.taskId === dependency.dependentEntityId
            ? {
                ...dep,
                dependencyType: DependencyTypesEnum[dependency.dependencyType],
                lag: dependency.lag,
              }
            : dep,
        );
      }

      recordData.predecessorDependencies =
        recordData.predecessorDependencies?.map((dep) => {
          if (dep.id === dependency.dependentEntityId) {
            return {
              ...dep,
              lag: dependency.lag,
              dependencyType: DependencyTypesEnum[dependency.dependencyType],
            };
          }

          return dep;
        });
    }
  });

  record.set('data', recordData);
}

function* onDependencyCreationDoneOnTaskThroughTimeline({
  payload,
}: PayloadAction<{
  id: number;
  type: EntityType;
  payload: {
    successorId: number;
    dependencyType: number;
    lag: number | null;
    predecessorId: number;
  };
  fromTemplate?: boolean;
}>): Generator {
  const { id, payload: depPayload, type, fromTemplate } = payload;

  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { dependencyStore, taskStore } = ganttRef?.instance;
  const ganttId =
    type === EntityType.Phase
      ? `${GanttType.ProjectPhaseBar}-${id}`
      : `${GanttType.TaskBar}-${id}`;
  const record = taskStore.getById(ganttId);
  const recordData = record.get('data');

  const { dependencyType, lag, predecessorId } = depPayload;

  const predecessorRecord =
    type === EntityType.Phase
      ? taskStore.getById(`${GanttType.ProjectPhaseBar}-${predecessorId}`)
      : taskStore.getById(`${GanttType.TaskBar}-${predecessorId}`);

  const predecessorRecordData = predecessorRecord.get('data');

  recordData.predecessorDependencies = [
    ...(recordData.predecessorDependencies || []),
    {
      wbs: predecessorRecordData.wbs,
      lag,
      dependencyType,
      id: predecessorId,
      name: predecessorRecordData.name,
    },
  ];

  if (type === EntityType.Phase) {
    if (fromTemplate) {
      recordData.dependentProjectPhases = [
        ...(recordData.dependentProjectPhases || []),
        {
          phaseId: predecessorId,
          dependencyType,
          lag,
          taskName: predecessorRecordData.name,
        },
      ];
    } else {
      recordData.dependencies = [
        ...(recordData.dependencies || []),
        {
          phaseId: predecessorId,
          dependencyType,
          lag,
          taskName: predecessorRecordData.name,
        },
      ];
    }
  } else if (fromTemplate) {
    recordData.dependentTasks = [
      ...(recordData.dependentTasks || []),
      {
        taskId: predecessorId,
        dependencyType,
        lag,
        taskName: predecessorRecordData.name,
      },
    ];
  } else {
    recordData.dependencies = [
      ...(recordData.dependencies || []),
      {
        taskId: predecessorId,
        dependencyType,
        lag,
        taskName: predecessorRecordData.name,
      },
    ];
  }

  record.set('data', recordData);

  const ganttType =
    type === EntityType.Phase ? GanttType.ProjectPhaseBar : GanttType.TaskBar;

  const dependencyData: {
    id: string;
    fromEvent: string;
    toEvent: string;
    type: number;
    lag?: number;
  } = {
    id: `${id}-${predecessorId}`,
    fromEvent: `${ganttType}-${predecessorId}`,
    toEvent: `${ganttType}-${id}`,
    type: dependencyType,
  };

  if (lag) {
    dependencyData.lag = lag;
  }

  dependencyStore.add(dependencyData);

  // Commit changes to ensure the UI reflects the changes
  ganttRef?.instance.project.commitAsync();
}

const createEmptyBaselines = (baselines: ProjectBaseline[]) => {
  return baselines.map((_baseline) => ({
    startDate: null,
    endDate: null,
  }));
};

function* onCreateSubTask(
  action: StoreActionProps<CreateSubTaskSuccessActionPayload>,
): Generator {
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { payload } = action;

  const { parentTask, task } = payload;
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(`${GanttType.TaskBar}-${parentTask}`);

  if (!record) {
    return;
  }
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const baselines = queryClient.getQueryData<ProjectBaseline[]>(
    getProjectBaselineQueryKey(task.project.projectId),
  );

  const recordData = record.get('data');
  const ganttTask = {
    ...convertToGanttTask(task as unknown as Task),
    baselines: baselines ? createEmptyBaselines(baselines) : [],
  };

  recordData.children.push(ganttTask.id);
  record.set('data', {
    ...recordData,
    children: recordData.children,
  });

  const queryTasks = queryClient.getQueryData<TaskApi[]>(
    getProjectTasksQueryKey(ProjectId(task.project.projectId)),
  );
  const project = queryClient.getQueryData<Project>(
    ProjectId(task.project.projectId),
  );

  if (!queryTasks || !project) {
    return;
  }
  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];
  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);

  // get all the list of children for the parent task
  const childrenTask = Object.values(taskList).filter(
    (t) => t.parent === parentTask,
  );

  // find the number of private tasks and public tasks
  const privateTasks = childrenTask.filter((t) => t.isPrivate);
  const publicTasks = childrenTask.filter((t) => !t.isPrivate);
  const childWbs = `${recordData.isPrivate ? 'P' : ''}${recordData.wbs}.${
    recordData.isPrivate ? privateTasks.length + 1 : publicTasks.length + 1
  }`;

  record.appendChild(
    convertToGanttStoreFormat(
      ganttTask,
      record.getStartDate(),
      recordData.rootIndex,
      recordData.currentLevel + 1,
      GanttType.TaskBar,
      childWbs,
    ),
  );
}

// Deprecated
// function* onDeleteTask({
//   payload,
// }: PayloadAction<FetchTaskActionPayload>): Generator {
//   const ganttRef = (yield getGanttRefContext()) as any;

//   if (!ganttRef) {
//     return;
//   }
//   const { taskId } = payload;
//   const { taskStore } = ganttRef?.instance;
//   const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);

//   if (!record) {
//     return;
//   }
//   const parentId = record.get('data').parent;
//   const parentRecord =
//     taskStore.getById(getGanttTaskId(parentId)) ||
//     taskStore.getById(getGanttPhaseId(parentId));

//   if (!parentRecord) {
//     return;
//   }
//   parentRecord.removeChild(record);
// }

function* onUpdateGanttSlack({
  payload,
}: PayloadAction<{
  projectId: number;
}>): Generator {
  const { projectId } = payload;
  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskStore } = ganttRef?.instance;

  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );
  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const newDependenciesSettingEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  if (!project || !project.startDate || !project.dueDate || !queryTasks) {
    return;
  }

  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];

  const _phaseList = getGanttPhaseList(projectPhases);
  const _taskList = getGanttTaskListWithoutBaselines(tasks ?? []);

  const [updatedPhaseList, updatedTaskList] = computeSlack(
    _phaseList,
    _taskList,
    project,
    newDependenciesSettingEnabled ?? false,
  );

  Object.values(updatedPhaseList).forEach(({ id, slack }) => {
    const record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${id}`);

    if (record) {
      record.set('totalSlack', slack);
      record.data.data.slack = slack;

      // Update critical classes
      const isCritical = slack <= 0;
      const currentCls = record.cls.toString();

      // Remove existing critical classes
      let updatedCls = currentCls
        .replace(/\bb-critical\b/g, '')
        .replace(/\bb-custom-critical\b/g, '')
        .trim();

      // Add critical classes if needed
      if (isCritical) {
        updatedCls += ' b-critical b-custom-critical';
      }

      record.cls = updatedCls;
    }
  });

  Object.values(updatedTaskList).forEach(({ id, slack }) => {
    const record = taskStore.getById(`${GanttType.TaskBar}-${id}`);

    if (record) {
      record.set('totalSlack', slack);
      record.data.data.slack = slack;

      // Update critical classes
      const isCritical = slack <= 0;
      const currentCls = record.cls.toString();

      // Remove existing critical classes
      let updatedCls = currentCls
        .replace(/\bb-critical\b/g, '')
        .replace(/\bb-custom-critical\b/g, '')
        .trim();

      // Add critical classes if needed
      if (isCritical) {
        updatedCls += ' b-critical b-custom-critical';
      }

      record.cls = updatedCls;
    }
  });
}

function* onRemoveAsSubtask({
  payload,
}: PayloadAction<RemoveAsSubtaskPayload>): Generator {
  const { taskId, phaseId } = payload;

  const ganttRef = (yield getGanttRefContext()) as any;

  if (!ganttRef) {
    return;
  }
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
  const recordData = record.get('data');

  if (!record) {
    return;
  }
  const parentId = record.get('data').parent;
  const parentRecord =
    taskStore.getById(getGanttTaskId(parentId)) ||
    taskStore.getById(getGanttPhaseId(parentId));

  if (!parentRecord) {
    return;
  }
  parentRecord.removeChild(record);

  const phaseRecord = taskStore.getById(getGanttPhaseId(phaseId as number));

  const phaseRecordData = phaseRecord.get('data');

  record.set('data', {
    ...recordData,
    currentLevel: phaseRecordData.currentLevel + 1,
    parent: phaseRecordData.id,
  });

  phaseRecord.appendChild(record);
}

function* updateProjectSprintDates(
  action: StoreActionProps<ProjectSprintDatesActionPayload>,
) {
  const { data, options } = action.payload;
  const { sprintId, startDate, dueDate } = data;

  try {
    const updateSprint = yield SprintPlanningAPI.updateSprint(
      sprintId,
      {
        startDate,
        dueDate,
      },
      options?.isSync,
    );

    if (updateSprint && options?.toggleUpdating) {
      options.toggleUpdating(false);
    }
  } catch (error) {
    message.error('There was an error processing your request.');
    logger.error('unable to update sprint dates', { error, data });
  }
}

function* updateTaskRecordWithLatestData({
  payload,
}: PayloadAction<UpdateTaskActionPayload>): Generator {
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }
  const { taskId, data } = payload;

  if (!data) {
    return;
  }

  const { assignee, completedAt } = data;

  const ganttId = `${GanttType.TaskBar}-${taskId}`;
  const { taskStore } = ganttRef?.instance;
  const record = taskStore.getById(ganttId);

  if (!record) {
    return;
  }

  const recordData = record.get('data');
  const recordDataUpdates = {} as GanttTask;

  if (assignee) {
    recordDataUpdates.assigneeUsers = assignee.users;
    recordDataUpdates.assigneeTeams = assignee.teams;
  }

  if (completedAt) {
    recordDataUpdates.completedAt = completedAt;
  } else {
    recordDataUpdates.completedAt = null;
  }

  record.set('data', {
    ...recordData,
    ...recordDataUpdates,
  });
}

function* deleteDependencyRelations({
  payload,
}: PayloadAction<{
  projectId: number;
  dependencyRelationsToDelete: string[];
}>): Generator {
  const { dependencyRelationsToDelete, projectId } = payload;
  const taskDependencyRelations: {
    taskId: number;
    dependentId: number;
  }[] = [];

  const phaseDependencyRelations: {
    phaseId: number;
    dependentId: number;
  }[] = [];
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const _projectId = ProjectId(projectId);
  const project = queryClient.getQueryData<Project>(_projectId);
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );

  if (!project || !queryTasks) {
    return;
  }

  const { projectPhases } = project;
  const tasks = normaliseTasks(queryTasks) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);

  dependencyRelationsToDelete.forEach((relation) => {
    const [id, dependentId] = relation.split('-');

    if (taskList[id]) {
      taskDependencyRelations.push({
        taskId: Number(id),
        dependentId: Number(dependentId),
      });
    }

    if (phaseList[id]) {
      phaseDependencyRelations.push({
        phaseId: Number(id),
        dependentId: Number(dependentId),
      });
    }
  });

  yield put(
    bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction({
      dependencyRelationsToDelete,
    }),
  );

  if (taskDependencyRelations.length > 0) {
    if (project && queryTasks) {
      const updatedTasks = queryTasks.map((task) => {
        const dependencyRelation = taskDependencyRelations.find(
          (relation) => relation.taskId === task.taskId,
        );

        if (dependencyRelation) {
          return {
            ...task,
            dependentTasks: task.dependentTasks.filter(
              (dependentTask) =>
                dependentTask.taskId !== dependencyRelation.dependentId,
            ),
          };
        }

        return task;
      });

      const queryKey = getProjectTasksQueryKey(ProjectId(projectId));

      queryClient.setQueryData(queryKey, updatedTasks);
    }
  }

  if (phaseDependencyRelations.length > 0) {
    if (project) {
      const { projectPhases: phases } = project;
      const updatedPhases = phases.map((phase) => {
        const dependencyRelation = phaseDependencyRelations.find(
          (relation) => relation.phaseId === phase.projectPhaseId,
        );

        if (dependencyRelation) {
          return {
            ...phase,
            dependentProjectPhases: phase.dependentProjectPhases
              ? phase.dependentProjectPhases.filter(
                  (dependentPhase) =>
                    dependentPhase.projectPhaseId !==
                    dependencyRelation.dependentId,
                )
              : [],
          };
        }

        return phase;
      });

      const queryKey = ProjectId(projectId);

      queryClient.setQueryData(queryKey, {
        ...project,
        projectPhases: updatedPhases,
      });
    }
  }
}

function* addTaskBelowSaga({
  payload,
}: PayloadAction<{
  projectId: number;
  taskId: number;
}>): Generator {
  const { projectId, taskId } = payload;

  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef?.instance) {
    return;
  }

  const { taskStore } = ganttRef.instance;
  const taskRecord = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
  const taskRecordData = taskRecord.get('data');

  const currentWbs = taskRecordData.wbs;
  const isSubtask = currentWbs && currentWbs.split('.').length > 2;
  const parentId = taskRecordData.parent;

  let parentRecord;
  let phaseId;

  if (isSubtask) {
    phaseId = taskRecordData.parentTaskObject.projectPhase.projectPhaseId;
    parentRecord = taskStore.getById(`${GanttType.TaskBar}-${parentId}`);
  } else {
    phaseId = parentId;
    parentRecord = taskStore.getById(
      `${GanttType.ProjectPhaseBar}-${parentId}`,
    );
  }

  if (!parentRecord) {
    return;
  }

  const parentRecordData = parentRecord.get('data');
  const currentTaskIndex = parentRecord.children.findIndex(
    (child) => child.id === taskRecord.id,
  );

  if (currentTaskIndex === -1) {
    return;
  }

  const uid = uniqueId();
  const newTaskRecord = {
    id: `${GanttType.AddNewTask}-${uid}`,
    cls: 'gantt-task gantt-bg-blue gantt-level-1',
    type: GanttType.AddNewTask,
    data: {
      id: uid,
      projectId,
      type: GanttType.AddNewTask,
      parent: parentId,
      children: [],
      assignees: [],
      currentLevel: isSubtask ? (parentRecordData.currentLevel || 1) + 1 : 1,
      ...(isSubtask && {
        parentTaskObject: {
          taskId: parentId,
          taskName: parentRecord.get('name'),
          projectPhase: {
            projectPhaseId: phaseId,
          },
        },
        private: parentRecordData.private || false,
      }),
      isSubtask,
      isPermanent: false,
      autoFocus: true,
    },
    children: [],
  };

  parentRecord.insertChild(
    newTaskRecord,
    parentRecord.children[currentTaskIndex + 1],
  );

  const updatedChildren = [...(parentRecordData.children || [])];

  updatedChildren.splice(currentTaskIndex + 1, 0, newTaskRecord.id);

  parentRecord.set('data', {
    ...parentRecordData,
    children: updatedChildren,
  });
}

function* addSubTaskSaga({
  payload,
}: PayloadAction<{
  projectId: number;
  taskId: number;
}>): Generator {
  const { projectId, taskId } = payload;

  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef?.instance) {
    return;
  }

  const { taskStore } = ganttRef.instance;
  const taskRecord = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);
  const taskRecordData = taskRecord.get('data');

  const uid = uniqueId();
  const newTaskRecord = {
    id: `${GanttType.AddNewTask}-${uid}`,
    cls: 'gantt-task gantt-bg-blue gantt-level-2',
    type: GanttType.AddNewTask,
    data: {
      id: uid,
      projectId,
      type: GanttType.AddNewTask,
      parent: taskId,
      isSubtask: true,
      parentTaskObject: {
        taskId,
        taskName: taskRecord.get('name'),
        projectPhase: {
          projectPhaseId:
            taskRecordData.parentTaskObject?.projectPhase?.projectPhaseId ||
            taskRecordData.parent,
        },
      },
      children: [],
      private: taskRecordData.private,
      assignees: [],
      currentLevel: (taskRecordData.currentLevel || 1) + 1,
      isPermanent: false,
      autoFocus: true,
    },
    children: [],
  };

  taskRecord.appendChild(newTaskRecord);

  const updatedChildren = [
    ...(taskRecordData.children || []),
    newTaskRecord.id,
  ];

  taskRecord.set('data', {
    ...taskRecordData,
    children: updatedChildren,
  });
}

function* removeTemporaryTaskSaga({
  payload,
}: PayloadAction<{
  recordId: string | number;
}>): Generator {
  const { recordId } = payload;

  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef?.instance) {
    return;
  }

  const { taskStore } = ganttRef.instance;
  const record = taskStore.getById(recordId);

  if (!record) {
    return;
  }

  const parentId = record.get('data').parent;
  const parentRecord =
    taskStore.getById(getGanttTaskId(parentId)) ||
    taskStore.getById(getGanttPhaseId(parentId));

  if (!parentRecord) {
    return;
  }

  parentRecord.removeChild(record);
}

function* updateSubtasksWbsRecursively({
  payload,
}: PayloadAction<{
  taskId: number;
  currentLevel: number;
  newNumber: string;
}>): Generator {
  const { taskId, currentLevel, newNumber } = payload;

  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }

  const { taskStore } = ganttRef.instance;
  const task = taskStore.getById(`${GanttType.TaskBar}-${taskId}`);

  if (!task) {
    return;
  }

  const children = task.children || [];

  if (children.length === 0) {
    return;
  }

  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    const childData = child.get('data');

    if (child.get('type') !== GanttType.AddNewTask) {
      const currentWbs = childData.wbs;
      const currentWbsParts = currentWbs.split('.');

      if (currentWbsParts.length > currentLevel) {
        currentWbsParts[currentLevel] = newNumber;

        const newWbs = currentWbsParts.join('.');

        child.set('data', {
          ...childData,
          wbs: newWbs,
        });

        if (child.children && child.children.length > 0) {
          // Call the recursive function directly to ensure synchronous execution
          yield* updateSubtasksWbsRecursively({
            payload: {
              taskId: childData.id,
              currentLevel,
              newNumber,
            },
            type: 'updateSubtasksWbsRecursively',
          });
        }
      }
    }
  }
}

function* bulkUpdateWbsNumbers({
  payload,
}: PayloadAction<{
  isSubtask: boolean;
  parentTaskId: number | undefined;
  actualApiPosition: number;
}>): Generator {
  const { isSubtask, parentTaskId, actualApiPosition } = payload;
  const ganttRef: any = yield call(getGanttRefContext);

  if (!ganttRef) {
    return;
  }

  const { taskStore } = ganttRef.instance;

  let record;
  let currentLevel = 1;
  let isPrivateTask = false;

  if (isSubtask) {
    record = taskStore.getById(`${GanttType.TaskBar}-${parentTaskId}`);
    isPrivateTask = record.get('data').private;
    currentLevel = record.get('data').currentLevel + 1;
  } else {
    record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${parentTaskId}`);
  }

  if (!record) {
    return;
  }

  const children = record.children || [];

  const realTasks = children.filter(
    (child) => child.get('type') !== GanttType.AddNewTask,
  );

  if (realTasks.length === 0 || actualApiPosition >= realTasks.length) {
    return;
  }

  taskStore.beginBatch();

  try {
    for (let i = actualApiPosition; i < realTasks.length; i += 1) {
      const task = realTasks[i];
      const taskData = task.get('data');

      if (taskData.private === isPrivateTask) {
        const currentWbs = taskData.wbs;
        const currentWbsParts = currentWbs.split('.');

        if (currentWbsParts.length > currentLevel) {
          const currentNumber = parseInt(currentWbsParts[currentLevel], 10);
          const newNumber = (currentNumber + 1).toString();

          currentWbsParts[currentLevel] = newNumber;

          const newWbs = currentWbsParts.join('.');

          task.set('data', {
            ...taskData,
            wbs: newWbs,
          });

          if (task.children && task.children.length > 0) {
            // Call the recursive function directly to ensure synchronous execution
            yield* updateSubtasksWbsRecursively({
              payload: {
                taskId: taskData.id,
                currentLevel,
                newNumber,
              },
              type: 'updateSubtasksWbsRecursively',
            });
          }
        }
      }
    }
  } finally {
    taskStore.endBatch();
  }
}

export function* cleanupTaskDependenciesAndRemoveFromTimeline(
  taskId: number,
): Generator {
  const ganttRef = (yield call(getGanttRefContext)) as any;
  const gantt = ganttRef?.instance;

  if (!gantt) {
    return;
  }

  const project = gantt.project ?? gantt; // depending on how you expose it
  const { taskStore, dependencyStore } = project;

  if (!taskStore || !dependencyStore) {
    return;
  }

  // IMPORTANT: use your helper so id format matches timeline records
  const recordId = getGanttTaskId(taskId);
  const taskRecord = taskStore.getById(recordId);

  if (!taskRecord) {
    return;
  }

  // Step 1: Clean up task data arrays (what the UI actually reads)
  // Find all tasks that have the deleted task in their dependentTasks array
  const allRecords = taskStore.records;

  if (allRecords && Array.isArray(allRecords)) {
    allRecords.forEach((otherRecord) => {
      if (!otherRecord || typeof otherRecord.get !== 'function') {
        return;
      }

      const otherRecordData = otherRecord.get('data');

      if (
        !otherRecordData ||
        !otherRecordData.id ||
        otherRecordData.id === taskId
      ) {
        return;
      }

      let needsUpdate = false;

      // Clean up dependentTasks array - remove references to the deleted task
      if (
        otherRecordData.dependentTasks &&
        Array.isArray(otherRecordData.dependentTasks)
      ) {
        const originalLength = otherRecordData.dependentTasks.length;

        otherRecordData.dependentTasks = otherRecordData.dependentTasks.filter(
          (dep) => dep && dep.taskId !== taskId,
        );
        if (otherRecordData.dependentTasks.length !== originalLength) {
          needsUpdate = true;
        }
      }

      // Clean up dependencies array (for phases/other dependency structures)
      if (
        otherRecordData.dependencies &&
        Array.isArray(otherRecordData.dependencies)
      ) {
        const originalLength = otherRecordData.dependencies.length;

        otherRecordData.dependencies = otherRecordData.dependencies.filter(
          (dep) => dep && dep.taskId !== taskId && dep.phaseId !== taskId,
        );
        if (otherRecordData.dependencies.length !== originalLength) {
          needsUpdate = true;
        }
      }

      // Clean up predecessorDependencies array
      if (
        otherRecordData.predecessorDependencies &&
        Array.isArray(otherRecordData.predecessorDependencies)
      ) {
        const originalLength = otherRecordData.predecessorDependencies.length;

        otherRecordData.predecessorDependencies =
          otherRecordData.predecessorDependencies.filter(
            (dep) => dep && dep.id !== taskId,
          );
        if (otherRecordData.predecessorDependencies.length !== originalLength) {
          needsUpdate = true;
        }
      }

      // Only update if we actually removed something
      if (needsUpdate) {
        otherRecord.set('data', otherRecordData);
      }
    });
  }

  // Step 2: Remove all incoming/outgoing deps from the dependency store
  // Dependencies are stored with IDs in format "successorId-predecessorId" (numeric IDs only)
  // Note: fromEvent/toEvent contain the full "TASK_BAR-{id}" format, but dep.id is just "id-id"
  const toRemove: any[] = [];

  dependencyStore.forEach((dep: any) => {
    const depId = dep.id;

    if (depId && typeof depId === 'string') {
      const parts = depId.split('-');

      // Handle both "id1-id2" format and potential edge cases
      if (parts.length >= 2) {
        const [successorIdStr, predecessorIdStr] = parts;
        const successorId = Number(successorIdStr);
        const predecessorId = Number(predecessorIdStr);

        // Check if this dependency involves our task (as either successor or predecessor)
        // Only match if the parsed IDs are valid numbers
        if (
          !Number.isNaN(successorId) &&
          !Number.isNaN(predecessorId) &&
          (successorId === taskId || predecessorId === taskId)
        ) {
          toRemove.push(dep);
        }
      }
    }
  });
  if (toRemove.length) {
    dependencyStore.remove(toRemove);
  }

  // Step 3: Finally remove the task from the store using parent-child relationship
  // (matching the original onDeleteTask approach)
  const recordData = taskRecord.get('data');

  if (recordData && recordData.parent) {
    const parentId = recordData.parent;
    const parentRecord =
      taskStore.getById(getGanttTaskId(parentId)) ||
      taskStore.getById(getGanttPhaseId(parentId));

    if (parentRecord) {
      parentRecord.removeChild(taskRecord);
    } else {
      // Fallback to direct removal if parent not found
      taskRecord.remove();
    }
  } else {
    taskRecord.remove();
  }
}

export const watchProjectTimelineSaga = function* (): Generator {
  const helpers: TimelineHelpersModule = yield call(
    loadProjectPlanTimelineHelpers,
  );

  ({
    computeSlack,
    convertToGanttStoreFormat,
    convertToGanttTask,
    getExtremeDates,
    getGanttPhaseList,
    getGanttTaskListWithoutBaselines,
  } = helpers);

  yield takeLatest(setGanttRefAction.type, setGanttRefContext);
  yield takeLatest(updateGanttDatesAction.type, onUpdateGanttDates);
  yield takeLatest(updateTaskSagaAction.type, onUpdateTask);
  yield takeLatest(updateSprintSagaAction.type, onUpdateSprint);
  yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
  yield takeLatest(dependencyRemovedAction.type, onDependencyRemoved);
  yield takeLatest(dependenciesRemovedAction.type, onDependeciesRemoved);
  yield takeLatest(createSubTaskSuccess.type, onCreateSubTask);
  // yield takeLatest(deleteTaskSagaAction.type, onDeleteTask); // no need to watch this
  yield takeLatest(updateGanttSlackAction.type, onUpdateGanttSlack);
  yield takeLatest(removeAsSubtaskSuccessAction.type, onRemoveAsSubtask);
  yield takeLatest(
    updateProjectSprintDatesAction.type,
    updateProjectSprintDates,
  );
  yield takeLatest(resetEntityDatesAction.type, resetEntityDates);
  yield takeLatest(updateTaskRecordSagaAction.type, onUpdateTask);
  yield takeLatest(
    updateTaskRecordWithLatestDataSagaAction.type,
    updateTaskRecordWithLatestData,
  );
  yield takeLatest(dependenciesOperationsAction.type, onDependencyOperations);
  yield takeLatest(
    dependencyCreationThroughTimeline.type,
    onDependencyCreationDoneOnTaskThroughTimeline,
  );
  yield takeLatest(
    bulkDependencyDeletionForPhasesAndTasksFromTimelineSagaAction.type,
    bulkDependencyDeletionForPhasesAndTasksFromTimeline,
  );
  yield takeLatest(
    deleteDependencyRelationsSagaAction.type,
    deleteDependencyRelations,
  );
  yield takeLatest(updateTaskPrivacyInGanttAction.type, onUpdateTaskPrivacy);
  yield takeLatest(addTaskBelowSagaAction.type, addTaskBelowSaga);
  yield takeLatest(addSubTaskSagaAction.type, addSubTaskSaga);
  yield takeLatest(removeTemporaryTaskSagaAction.type, removeTemporaryTaskSaga);
  yield takeLatest(bulkUpdateWbsSagaAction.type, bulkUpdateWbsNumbers);
};
